import { useState, useEffect, useCallback } from 'react';
import { analyzeArticle, getAssetSparkline, getDomains } from '../api';
import ScoreBar from './ScoreBar';
import DirectionBadge from './DirectionBadge';
import Sparkline from './Sparkline';

const CONF_CLS = {
  HIGH:   'bg-green-500/15 text-green-400',
  MEDIUM: 'bg-yellow-500/15 text-yellow-400',
  LOW:    'bg-slate-500/15 text-slate-400',
};

const ROW_BORDER = {
  BULLISH: 'border-l-2 border-l-green-500 bg-green-500/[0.02]',
  BEARISH: 'border-l-2 border-l-red-500 bg-red-500/[0.02]',
  NEUTRAL: 'border-l-2 border-l-slate-600',
};

function AssetRow({ asset }) {
  const [sparkline, setSparkline] = useState([]);
  const [showExplain, setShowExplain] = useState(false);

  useEffect(() => {
    getAssetSparkline(asset.symbol).then(d => setSparkline(d.scores || [])).catch(() => {});
  }, [asset.symbol]);

  return (
    <>
      <tr className={`${ROW_BORDER[asset.direction] || ROW_BORDER.NEUTRAL} hover:bg-slate-100 transition-colors`}>
        <td className="px-4 py-3">
          <div className="flex flex-col">
            <span className="font-bold text-slate-100">{asset.symbol}</span>
            <span className="text-xs text-slate-500">{asset.name}</span>
          </div>
        </td>
        <td className="px-4 py-3"><DirectionBadge direction={asset.direction} /></td>
        <td className="px-4 py-3"><ScoreBar score={asset.score} /></td>
        <td className="px-4 py-3">
          <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold tracking-wide ${CONF_CLS[asset.confidence] || CONF_CLS.LOW}`}>
            {asset.confidence}
          </span>
        </td>
        <td className="px-4 py-3 text-xs text-slate-400 max-w-xs">{asset.reason}</td>
        <td className="px-4 py-3"><Sparkline scores={sparkline} /></td>
        <td className="px-4 py-3">
          <button
            onClick={() => setShowExplain(v => !v)}
            className="bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-500 hover:text-slate-700 rounded-lg transition-colors cursor-pointer text-xs px-2 py-1"
          >
            {showExplain ? 'Hide' : 'Why?'}
          </button>
        </td>
      </tr>
      {showExplain && (
        <tr>
          <td colSpan={7} className="px-4 py-3 bg-slate-100">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span className="font-semibold text-slate-700">Key phrases:</span>
              {(asset.key_phrases || []).length === 0
                ? <span>None extracted</span>
                : asset.key_phrases.map((p, i) => (
                    <span key={i} className="bg-white border border-slate-200 text-primaryDark px-2 py-0.5 rounded">"{p}"</span>
                  ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const URL_RE = /^https?:\/\/.{4,}/;

export default function SubmitTab() {
  const [inputMode, setInputMode] = useState('text'); // 'text' | 'url'
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [assetFilter, setAssetFilter] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [domainId, setDomainId] = useState('crypto-news');
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getDomains().then(d => setDomains(d.domains || [])).catch(() => {});
  }, []);

  const handleSubmit = useCallback(async () => {
    setLoading(true); setError(null); setErrorStatus(null); setResult(null);
    const filter = assetFilter.trim()
      ? assetFilter.split(',').map(s => s.trim().toUpperCase()).filter(Boolean)
      : null;
    try {
      const body = inputMode === 'url'
        ? { article_text: '', source_url: url.trim(), asset_filter: filter, source_name: sourceName || null, domain_id: domainId }
        : { article_text: text, asset_filter: filter, source_name: sourceName || null, domain_id: domainId };
      const data = await analyzeArticle(body);
      setResult(data);
    } catch (err) {
      setError(err.message || 'Analysis failed. Please try again.');
      setErrorStatus(err.status || null);
    } finally {
      setLoading(false);
    }
  }, [inputMode, text, url, assetFilter, sourceName, domainId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  const canSubmit = !loading && (
    inputMode === 'text' ? text.trim().length >= 100 : URL_RE.test(url.trim())
  );

  return (
    <div className="h-screen w-screen overflow-hidden bg-white">
      <div className="flex h-full w-full">
        <aside className="h-full w-[30%] min-w-0 border-r border-slate-200 bg-white overflow-y-auto">
          <div className="h-full p-5">
            <h2 className="text-lg font-bold text-primary mb-1">Analyze Article</h2>
            <p className="text-sm text-slate-500 mb-5">
              Paste article text or provide a URL - Claude will extract and analyze the content.
            </p>

            <div className="flex gap-1 mb-4 p-1 bg-slate-100 rounded-lg w-fit">
              {[['text', 'Paste Text'], ['url', 'Enter URL']].map(([mode, label]) => (
                <button
                  key={mode}
                  onClick={() => { setInputMode(mode); setResult(null); setError(null); setErrorStatus(null); }}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    inputMode === mode
                      ? 'bg-secondaryLight text-white'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {inputMode === 'text' && (
              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Article Text <span className="text-red-400 normal-case">*</span></label>
                <textarea
                  rows={14}
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder="Paste the full text of the financial news article here..."
                  className="bg-white border border-slate-300 focus:border-primary focus:outline-none rounded-lg text-slate-800 text-sm px-3 py-2 transition-colors w-full resize-y min-h-[240px] font-mono text-xs leading-relaxed"
                />
                <p className={`text-xs mt-1 ${text.length < 100 ? 'text-red-400' : 'text-slate-500'}`}>
                  {wordCount} words{text.length < 100 && ' - minimum 100 characters required'}
                </p>
              </div>
            )}

            {inputMode === 'url' && (
              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Article URL <span className="text-red-400 normal-case">*</span></label>
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://example.com/article"
                  className="bg-white border border-slate-300 focus:border-primary focus:outline-none rounded-lg text-slate-800 text-sm px-3 py-2 transition-colors w-full"
                />
                <p className={`text-xs mt-1 ${!URL_RE.test(url.trim()) && url.length > 0 ? 'text-red-400' : 'text-slate-500'}`}>
                  {url.length === 0
                    ? 'Enter a full URL starting with http:// or https://'
                    : !URL_RE.test(url.trim())
                      ? 'Please enter a valid URL'
                      : 'Article content will be fetched and analyzed automatically'}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 mb-5">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Focus on assets (optional)</label>
                <input type="text" value={assetFilter} onChange={e => setAssetFilter(e.target.value)}
                  placeholder="e.g. BTC, ETH" className="bg-white border border-slate-300 focus:border-primary focus:outline-none rounded-lg text-slate-800 text-sm px-3 py-2 transition-colors w-full" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Source name (optional)</label>
                <input type="text" value={sourceName} onChange={e => setSourceName(e.target.value)}
                  placeholder="e.g. CoinDesk, Reuters" className="bg-white border border-slate-300 focus:border-primary focus:outline-none rounded-lg text-slate-800 text-sm px-3 py-2 transition-colors w-full" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Domain</label>
                <select value={domainId} onChange={e => setDomainId(e.target.value)} className="bg-white border border-slate-300 focus:border-primary focus:outline-none rounded-lg text-slate-800 text-sm px-3 py-2 transition-colors w-full">
                  {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>

            <button className="bg-secondaryLight hover:bg-secondaryDark disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5 rounded-lg transition-colors cursor-pointer w-full" onClick={handleSubmit} disabled={!canSubmit}>
              {loading ? 'Analyzing...' : 'Analyze Article'}
            </button>
          </div>
        </aside>

        <section className="h-full w-[70%] min-w-0 bg-white overflow-y-auto">
          <div className="h-full">
            {loading && (
              <div className="h-full flex items-center justify-center px-6">
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-6 py-4 flex items-center gap-3 text-slate-400">
                  <div className="w-5 h-5 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin flex-shrink-0" />
                  Analyzing - this takes 5 to 15 seconds...
                </div>
              </div>
            )}

            {!loading && error && (
              <div className="h-full flex items-start px-6 py-6">
                <div className="w-full rounded-xl border border-red-500/40 bg-red-500/10 px-5 py-4">
                  <p className="text-red-700 font-semibold text-sm">Analysis failed: {error}</p>
                  {inputMode === 'url' && (errorStatus === 422 || errorStatus === 502) ? (
                    <ul className="text-red-600 text-xs mt-2 space-y-0.5 list-disc list-inside">
                      <li>Try a different URL or source for the same article</li>
                      <li>Switch to <strong>Paste Text</strong> mode and paste the article content directly</li>
                      <li>Some sites block automated access — copying the text is the most reliable option</li>
                    </ul>
                  ) : (
                    <p className="text-red-600 text-xs mt-1">Check your API key and network connection, then try again.</p>
                  )}
                </div>
              </div>
            )}

            {!loading && !error && !result && (
              <div className="h-full flex items-center justify-center px-6 text-center">
                <div className="flex flex-col items-center">
                  <div className="mb-3 rounded-xl border border-slate-200 bg-slate-100 p-3 shadow-sm">
                    <svg
                      className="h-7 w-7 text-slate-600"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M6 3h9l4 4v14H6z" />
                      <path d="M15 3v4h4" />
                      <path d="M9 11h6" />
                      <path d="M9 15h6" />
                      <path d="M9 19h4" />
                    </svg>
                  </div>
                  <p className="text-slate-700 text-base font-semibold">Results will appear here</p>
                  <p className="text-slate-500 text-sm mt-1">Submit an article from the left panel to view sentiment and asset analysis.</p>
                </div>
              </div>
            )}

            {!loading && !error && result && (
              <div className="h-full border-l border-slate-200">
                {result.cached ? (
                  <div className="px-5 py-3 bg-blue-500/10 border-b border-blue-500/30 text-primaryDark text-sm">
                    Cached result - originally analysed at {new Date(result.analysed_at).toLocaleString()}
                  </div>
                ) : (
                  <div className="px-5 py-3 bg-green-500/10 border-b border-green-500/30 text-green-600 text-sm">
                    Analysis complete - {result.assets?.length || 0} asset{result.assets?.length !== 1 ? 's' : ''} detected
                  </div>
                )}

                <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-3 border-b border-slate-300">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-slate-500">Overall:</span>
                    <DirectionBadge direction={result.article_direction} size="lg" />
                    <span className="text-xs text-slate-500">
                      Model: {result.model_used?.split('-').slice(-2).join('-')}
                    </span>
                    <span className="text-xs text-slate-500">Cost: ${(result.cost_usd || 0).toFixed(5)}</span>
                    <span className="text-xs text-slate-500">
                      Tokens: {(result.input_tokens || 0) + (result.output_tokens || 0)}
                    </span>
                  </div>
                  <button className="bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg text-sm transition-colors cursor-pointer" onClick={handleCopy}>
                    {copied ? 'Copied!' : 'Copy JSON'}
                  </button>
                </div>

                {result.assets?.length === 0 ? (
                  <p className="px-5 py-8 text-slate-500 text-sm text-center">
                    No financial assets detected in this article.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-300">
                          {['Asset', 'Direction', 'Score', 'Confidence', 'Reason', 'Trend', ''].map(h => (
                            <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {(result.assets || []).map(asset => (
                          <AssetRow key={asset.symbol} asset={asset} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
